@layer components {
  .embedded {
    padding-block-start: var(--space-2);
  }

  .problem {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-lg);
    background: var(--color-danger-soft);
    color: var(--color-danger);

    & > button {
      grid-column: 1 / -1;
    }

    @container (inline-size >= 30rem) {
      grid-template-columns: auto minmax(0, 1fr) auto;

      & > button {
        grid-column: auto;
      }
    }
  }

  .problemIcon {
    inline-size: 1.25rem;
    block-size: 1.25rem;
    stroke-width: 2.2;
  }

  .problemText {
    font-size: var(--text-sm);
    font-weight: 520;
    overflow-wrap: anywhere;

    & strong {
      font-weight: 700;
    }
  }

  .empty {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3) var(--space-4);

    @container (inline-size >= 34rem) {
      grid-template-columns: auto minmax(0, 1fr) auto;
    }
  }

  .emptyMark {
    display: grid;
    place-items: center;
    align-self: start;
    inline-size: 3rem;
    block-size: 3rem;
    border-radius: var(--radius-blob);
    --candy: var(--sticker-text);

    background: var(--candy-gloss), var(--candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -4px 6px -2px color-mix(in oklch, var(--candy), black 22%),
      var(--shadow-candy-drop-sm);
    rotate: -6deg;

    & svg {
      inline-size: 1.375rem;
      block-size: 1.375rem;
      stroke-width: 2.2;
    }

    @container (inline-size >= 34rem) {
      align-self: center;
    }
  }

  .emptyText {
    display: grid;
    gap: var(--space-1);
    min-inline-size: 0;
  }

  .emptyTitle {
    font-size: var(--text-base);
    font-weight: 650;
  }

  .emptyDetail {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    text-wrap: pretty;
  }

  .emptyAction {
    grid-column: 1 / -1;

    @container (inline-size >= 34rem) {
      grid-column: auto;
    }
  }

  .list {
    container-type: inline-size;
    display: grid;
    gap: var(--space-1);
    margin: 0 calc(-1 * var(--space-2));
    padding: 0;
    list-style: none;
  }

  .row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas:
      "mark text"
      "actions actions";
    gap: var(--space-3);
    position: relative;
    padding: var(--space-3) var(--space-2);
    border-radius: var(--radius-lg);

    & + &::before {
      content: "";
      position: absolute;
      inset-inline: var(--space-2);
      inset-block-start: calc(-0.5 * var(--space-1));
      border-block-start: 1px solid var(--color-border);
    }

    &[data-fresh] {
      background: var(--color-success-soft);
      animation: fresh-in var(--duration-spring) var(--ease-spring);
    }

    &[data-fresh]::before,
    &[data-fresh] + &::before {
      content: none;
    }

    @container (inline-size >= 34rem) {
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-areas: "mark text actions";
      align-items: center;
      column-gap: var(--space-4);
    }
  }

  @keyframes fresh-in {
    from {
      scale: 0.96;
      opacity: 0.4;
    }
  }

  .mark {
    grid-area: mark;
    display: grid;
    place-items: center;
    align-self: start;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border-radius: var(--radius-blob);
    --candy: var(--sticker-folder);

    background: var(--candy-gloss), var(--candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -4px 6px -2px color-mix(in oklch, var(--candy), black 22%),
      var(--shadow-candy-drop-sm);
    rotate: -5deg;

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.3;
    }

    &[data-mode="stream"] {
      --candy: var(--sticker-audio);

      rotate: 5deg;
    }
  }

  .rowText {
    grid-area: text;
    display: grid;
    justify-items: start;
    gap: var(--space-2);
    min-inline-size: 0;
  }

  .freshNote {
    color: var(--color-success);
    font-size: var(--text-xs);
    font-weight: 700;
  }

  .url {
    display: inline-flex;
    align-items: center;
    max-inline-size: 100%;
    min-block-size: 1.75rem;
    color: var(--color-text);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: 560;
    line-height: 1.4;
    text-decoration: none;
    overflow-wrap: anywhere;

    @media (hover: hover) {
      &:hover {
        color: var(--color-accent);
        text-decoration: underline;
        text-underline-offset: 0.2em;
      }
    }

    &[data-full] {
      user-select: all;
    }
  }

  .badges,
  .facts {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1) var(--space-2);
    padding: 0;
    list-style: none;
  }

  .badges li {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-block-size: 1.625rem;
    padding: 0.1rem 0.6rem 0.1rem 0.45rem;
    border-radius: var(--radius-full);
    background: var(--color-surface-sunken);
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &[data-tone="stream"] {
      background: color-mix(in oklab, var(--sticker-audio), var(--color-surface) 45%);
      color: var(--sticker-ink);
    }

    &[data-tone="password"] {
      background: var(--color-accent-soft);
      color: var(--color-accent);
    }
  }

  .badgeIcon {
    flex: none;
    inline-size: 0.875rem;
    block-size: 0.875rem;
    stroke-width: 2.4;
  }

  /* Each line's leading dot sits in the clipped strip, so a wrapped line never starts with one. */
  .facts {
    --dot: 1.25rem;

    gap: 0;
    margin-inline-start: calc(-1 * var(--dot));
    clip-path: inset(-0.5rem -0.5rem -0.5rem var(--dot));
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;

    & li {
      display: inline-flex;
      align-items: baseline;
      white-space: nowrap;
    }

    & li::before {
      content: "·";
      flex: none;
      inline-size: var(--dot);
      color: var(--color-text-faint);
      text-align: center;
    }

    & li[data-soon] {
      color: var(--color-danger);
      font-weight: 620;
    }
  }

  .moment {
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-decoration: underline dotted color-mix(in oklch, currentColor, transparent 55%);
    text-underline-offset: 0.2em;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;

    @media (hover: hover) {
      &:hover {
        color: var(--color-text);
      }
    }

    &:focus-visible {
      border-radius: 0.25rem;
      outline: 2px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .rowActions {
    grid-area: actions;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-2);

    @container (inline-size >= 34rem) {
      display: flex;
    }
  }

  .rowActions > .copy[data-variant="ghost"] {
    --button-bg: var(--color-accent-soft);
    --button-fg: var(--color-accent);
  }

  .rowActions > .revoke {
    --button-fg: var(--color-danger);
    --button-edge: color-mix(in oklch, var(--color-danger), transparent 70%);
    --button-shade: color-mix(in oklch, var(--color-danger), transparent 85%);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        --button-bg: var(--color-danger-soft);
        --button-edge: var(--color-danger);
      }
    }
  }

  .addRow {
    padding-block-start: var(--space-1);
  }

  .add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    inline-size: 100%;
    min-block-size: 3rem;
    border: 2px dashed var(--color-border-strong);
    border-radius: var(--radius-lg);
    background: transparent;
    color: var(--color-text-muted);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 650;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      border-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.4;
    }

    @media (hover: hover) {
      &:hover {
        border-color: var(--color-accent);
        background: var(--color-accent-soft);
        color: var(--color-accent);
      }
    }

    &:active {
      scale: 0.98;
      transition-duration: 60ms;
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .ended {
    border-block-start: 1px solid var(--color-border);
    padding-block-start: var(--space-3);
  }

  .endedSummary {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-block-size: var(--tap-target);
    margin-inline-start: calc(-1 * var(--space-2));
    padding-inline: var(--space-2) var(--space-3);
    border-radius: var(--radius-full);
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 620;
    cursor: pointer;
    list-style: none;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out);
    -webkit-tap-highlight-color: transparent;

    &::-webkit-details-marker {
      display: none;
    }

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);
        color: var(--color-text);
      }
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .endedCount {
    display: grid;
    place-items: center;
    min-inline-size: 1.5rem;
    block-size: 1.5rem;
    padding-inline: var(--space-2);
    border-radius: var(--radius-full);
    background: var(--color-surface-sunken);
    font-size: var(--text-xs);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .endedList {
    container-type: inline-size;
    display: grid;
    margin-block-start: var(--space-1);
    padding: 0;
    list-style: none;
  }

  .endedRow {
    display: grid;
    gap: 0.125rem;
    padding-block: var(--space-2);
    color: var(--color-text-muted);
    font-size: var(--text-sm);

    & + & {
      border-block-start: 1px solid var(--color-border);
    }

    @container (inline-size >= 26rem) {
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: baseline;
      gap: var(--space-3);
    }
  }

  .endedUrl {
    font-family: var(--font-mono);
    text-decoration: line-through;
    text-decoration-color: color-mix(in oklch, currentColor, transparent 55%);
    overflow-wrap: anywhere;
  }

  .endedWhen {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .endedChevron {
    inline-size: 1rem;
    block-size: 1rem;
    stroke-width: 2.4;
    transition: rotate var(--duration-spring) var(--ease-spring);

    [open] > summary > & {
      rotate: 90deg;
    }
  }

  .dialogUrl {
    font-family: var(--font-mono);
    font-size: 0.95em;
    overflow-wrap: anywhere;
  }

  .form {
    container-type: inline-size;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-5);
    animation: fresh-in var(--duration-spring) var(--ease-spring);
  }

  @container (inline-size >= 40rem) {
    .form {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-5) var(--space-6);
    }

    .field[data-wide],
    .formError,
    .formActions {
      grid-column: 1 / -1;
    }
  }

  .field {
    display: grid;
    align-content: start;
    gap: var(--space-2);
    min-inline-size: 0;
  }

  .modeOptions {
    align-self: start;
  }

  .labelRow {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }

  .optional {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 560;
  }

  .hint {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 540;
    font-variant-numeric: tabular-nums;

    &[data-error] {
      color: var(--color-danger);
      font-size: var(--text-sm);
      font-weight: 620;
    }
  }

  .segmented {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    padding: var(--space-1);
    border-radius: var(--radius-lg);
    background: var(--color-surface-sunken);
    box-shadow: var(--shadow-well);
  }

  .segment {
    position: relative;
    display: grid;
    flex: 1 1 auto;
    place-items: center;
    min-block-size: 2.625rem;
    padding-inline: var(--space-3);
    border-radius: calc(var(--radius-lg) - var(--space-1));
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 620;
    white-space: nowrap;
    cursor: pointer;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out),
      box-shadow var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover:not([data-checked]) {
        background: var(--color-surface-hover);
        color: var(--color-text);
      }
    }

    &:active:not([data-checked]) {
      scale: 0.97;
      transition-duration: 60ms;
    }

    &[data-checked] {
      background: var(--color-surface);
      color: var(--color-accent);
      box-shadow:
        inset 0 -2px 0 var(--color-border),
        var(--shadow-sm);
      cursor: default;
    }

    &:has(:focus-visible) {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .expiryDetail {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2) var(--space-3);

    & > .hint {
      flex: 1 1 12rem;
    }
  }

  .expiryDetail > .dateInput {
    flex: 0 0 auto;
    inline-size: 11.5rem;
    font-variant-numeric: tabular-nums;
  }

  .passwordField {
    position: relative;
    display: grid;
  }

  .passwordField > .passwordInput {
    padding-inline-end: var(--tap-target);
  }

  .reveal {
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    display: grid;
    place-items: center;
    inline-size: var(--tap-target);
    border: 0;
    border-radius: var(--radius-md);
    background: none;
    color: var(--color-text-muted);
    -webkit-tap-highlight-color: transparent;

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.1;
    }

    @media (hover: hover) {
      &:hover {
        color: var(--color-accent);
      }
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: -5px;
    }
  }

  .formError {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--color-danger);
    font-size: var(--text-sm);
    font-weight: 560;

    & svg {
      flex: none;
      inline-size: 1.125rem;
      block-size: 1.125rem;
    }
  }

  .formActions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-2);

    @container (inline-size >= 26rem) {
      display: flex;
      justify-content: flex-end;
    }
  }
}
